/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  customerService,
  type AppUser,
  type CustomerUserDetails,
  type CreateAppUserBody,
  type GetAppUsersParams,
  type UpdateAppUserBody,
} from "@src/@api-services/customer";
import type { UserType } from "../constants";
import { USER_TYPES } from "../constants";
import { SortOrder } from "@src/shared/constants/sort-order.constants";

interface CustomerUsersState {
  data: AppUser[];
  total: number;
  params: GetAppUsersParams & { userType?: UserType };
  selectedAppUser: AppUser | CustomerUserDetails | null;
  listLoading: boolean;
  detailLoading: boolean;
  saving: boolean;
  error: string | null;
}

const unwrapList = (response: any) => {
  const payload = response?.data?.data ?? response?.data ?? {};
  return {
    data: (payload.data ?? []) as AppUser[],
    total: Number(payload.totalRecord ?? payload.totalRecord ?? 0),
  };
};

export const getCustomerUsers = createAsyncThunk(
  "customerUsers/getCustomerUsers",
  async (
    params: GetAppUsersParams & { userType: UserType },
    { rejectWithValue }
  ) => {
    try {
      const { userType, ...query } = params;
      const response =
        userType === USER_TYPES.business.value
          ? await customerService.getBusinessUsers(query)
          : await customerService.getAppUsers(query);

      const { data, total } = unwrapList(response);
      return { params, data, total };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch users"
      );
    }
  }
);

export const getAppUser = createAsyncThunk(
  "customerUsers/getAppUser",
  async (
    { id, userType }: { id: string | number; userType: UserType },
    { rejectWithValue }
  ) => {
    try {
      const response =
        userType === USER_TYPES.business.value
          ? await customerService.getBusinessUsersDetails(id)
          : await customerService.getAppUser(id);
      return response.data.data as AppUser | CustomerUserDetails;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch user"
      );
    }
  }
);

export const addAppUser = createAsyncThunk(
  "customerUsers/addAppUser",
  async (body: CreateAppUserBody, { rejectWithValue }: any) => {
    try {
      const response = await customerService.createAppUser(body);
      return response.data?.message || "User created successfully.";
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create user"
      );
    }
  }
);

export const updateAppUser = createAsyncThunk(
  "customerUsers/updateAppUser",
  async (
    { id, body }: { id: string | number; body: UpdateAppUserBody },
    { rejectWithValue }: any
  ) => {
    try {
      const response = await customerService.updateAppUser(id, body);
      return response.data?.message || "User updated successfully.";
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update user"
      );
    }
  }
);

export const updateAppUserStatus = createAsyncThunk(
  "customerUsers/updateAppUserStatus",
  async (
    { id, status }: { id: string | number; status: string },
    { rejectWithValue }: any
  ) => {
    try {
      const response = await customerService.updateAppUserStatus(id, status);
      return response.data?.message || "Status updated successfully.";
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update status"
      );
    }
  }
);

export const deleteAppUser = createAsyncThunk(
  "customerUsers/deleteAppUser",
  async (id: string | number, { rejectWithValue }: any) => {
    try {
      const response = await customerService.deleteAppUser(id);
      return response.data?.message || id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete user"
      );
    }
  }
);

const initialState: CustomerUsersState = {
  data: [],
  total: 0,
  params: { page: 1, pageSize: 10, orderBy: "createdAt", orderDirection: SortOrder.DESC },
  selectedAppUser: null,
  listLoading: false,
  detailLoading: false,
  saving: false,
  error: null,
};

export const customerUsersSlice = createSlice({
  name: "customerUsers",
  initialState,
  reducers: {
    clearCustomerError: (state) => {
      state.error = null;
    },
    clearSelectedAppUser: (state) => {
      state.selectedAppUser = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCustomerUsers.pending, (state) => {
        state.listLoading = true;
        state.error = null;
      })
      .addCase(getCustomerUsers.fulfilled, (state, action) => {
        state.listLoading = false;
        state.data = action.payload.data;
        state.total = action.payload.total;
        state.params = action.payload.params;
      })
      .addCase(getCustomerUsers.rejected, (state, action) => {
        state.listLoading = false;
        state.error = action.payload as string;
        state.data = [];
        state.total = 0;
      })
      .addCase(getAppUser.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(getAppUser.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.selectedAppUser = action.payload;
      })
      .addCase(getAppUser.rejected, (state) => {
        state.detailLoading = false;
        state.selectedAppUser = null;
      })
      .addMatcher(
        (action) =>
          action.type.endsWith("/pending") &&
          ["addAppUser", "updateAppUser", "updateAppUserStatus", "deleteAppUser"].some(
            (type) => action.type.includes(type)
          ),
        (state) => {
          state.saving = true;
          state.error = null;
        }
      )
      .addMatcher(
        (action) =>
          action.type.endsWith("/fulfilled") &&
          ["addAppUser", "updateAppUser", "updateAppUserStatus", "deleteAppUser"].some(
            (type) => action.type.includes(type)
          ),
        (state) => {
          state.saving = false;
        }
      )
      .addMatcher(
        (action) =>
          action.type.endsWith("/rejected") &&
          ["addAppUser", "updateAppUser", "updateAppUserStatus", "deleteAppUser"].some(
            (type) => action.type.includes(type)
          ),
        (state, action: any) => {
          state.saving = false;
          state.error = action?.payload as string;
        }
      );
  },
});

export const { clearCustomerError, clearSelectedAppUser } =
  customerUsersSlice.actions;

export default customerUsersSlice.reducer;
